<template>
  <v-app :class="[appClasses, { 'input-selection-indeterminate': subscriptionBranding && subscriptionBranding.color }]">
    <transition name="app-fade">
      <div v-if="shouldDisplayNavigationDrawer">
        <ProjectsSidebar v-if="homeLayout" />
        <ProjectSidebar v-else-if="projectLayout" />
      </div>
    </transition>

    <AppBar v-if="!isRouteWithoutConnectedLayout && usesConnectedLayout" :fullAccessScope="isFullAccessLogin" />

    <v-main>
      <div class="d-flex flex-column fill-height">
        <AppBarRoomBreadcrumb v-if="shouldDisplayRoomBreadcrumb" :room="currentRoom" :currentNavigationPage="currentRoomNavigationPage" />
        <AppFileUpload v-if="isFullAccessLogin"/>

        <div class="app-main-content">
          <div v-if="isGlobalLoaderOn" class="fill-height d-flex align-center justify-center">
            <AppLoader />
          </div>
          <router-view v-slot="{ Component }" v-else-if="appIsInitialized && !unauthorizedState" class="d-flex flex-column">
            <transition name="app-fade">
              <component :is="Component" />
            </transition>
          </router-view>

          <p v-if="appIsInError" class="d-flex align-center justify-center h-100">
            {{$t('App.initError')}}
          </p>
        </div>
        <AppFooter v-if="usesConnectedLayout && $route.path === '/' && !$vuetify.display.xs"/>
      </div>
    </v-main>

    <AppToastsStack />

    <Maintenance v-if="maintenanceModeEnabled"/>

    <!--
      This is based on the assertion that when on a subdomain and going to an unauthorized space,
      you'll soon be redirected by the following logic. Thus we avoid the "overlay flash" that would happen otherwise.
    -->
    <UnauthorizedOverlay v-if="appIsInitialized && unauthorizedState && !navigatingOnAvailableSubdomain"/>
    <AppVersionChecker v-if="isFullAccessLogin && usesConnectedLayout" @reload="appState = AppStateEnum.loading" />

    <IdentityCheckingDialog v-if="usesConnectedLayout && showIdentityCheckModal && isFullAccessLogin"
                            @close="SET_SHOW_IDENTITY_CHECK_MODAL(false)"
    />
    <IdentityCheckingLandingDialog
      v-if="usesConnectedLayout && showIdentityCheckLandingModal && isFullAccessLogin && isPreviousRouteLogin"
      @close="SET_SHOW_IDENTITY_CHECK_LANDING_MODAL(false)"
    />
    <ProjectTypeQuoteRequestDialog
      v-if="projectTypeQuoteRequestDialog.isOpen"
      :default-project-type="projectTypeQuoteRequestDialog.defaultProjectType || 'PR'"
      @close="SET_PROJECT_TYPE_QUOTE_REQUEST_DIALOG({ isOpen: false })"
    />
    <DiscoverElectronicSafeDialog
      v-if="archivesStore?.discoverElectronicSafeDialogIsOpen"
      @close="archivesStore.setDiscoverElectronicSafeDialogIsOpen(false)"
      @requestActivation="openRequestElectronicSafeQuoteDialog"
    />

    <AIAssistantPane v-if="isFullAccessLogin" />

    <transition name="upload-hint-fade">
      <AppFileUploadHint v-if="uploadHint.visible" />
    </transition>
  </v-app>
</template>

<script>
import * as Sentry from '@sentry/vue'
import dayjs from 'dayjs'
import { isObject } from 'lodash-es'
import { isNavigationFailure } from 'vue-router'
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex'

import AIAssistantPane from '@/common/ai-assistant/AIAssistantPane.vue'
import { initMatomo } from '@/common/analytics/matomo'
import AppBar from '@/common/app-bar/AppBar.vue'
import AppBarRoomBreadcrumb from '@/common/app-bar/AppBarRoomBreadcrumb.vue'
import AppFileUpload from '@/common/app-file-upload/AppFileUpload.vue'
import AppFileUploadHint from '@/common/app-file-upload/AppFileUploadHint.vue'
import AppFooter from '@/common/AppFooter.vue'
import AppLoader from '@/common/AppLoader.vue'
import AppToastsStack from '@/common/AppToastsStack.vue'
import AppVersionChecker from '@/common/AppVersionChecker.vue'
import { TWO_FACTOR_AUTH_REQUIRED_ERROR } from '@/common/constants'
import IdentityCheckingDialog from '@/common/identity-checking/IdentityCheckingDialog.vue'
import IdentityCheckingLandingDialog from '@/common/identity-checking/IdentityCheckingLandingDialog.vue'
import { getRoomNavigationDataFromRoute } from '@/common/navigation-drawer/roomNavigationData.js'
import UnauthorizedOverlay from '@/common/UnauthorizedOverlay.vue'
import { getUserId, removeUserId, setUserId } from '@/common/utils/localUserId'
import { QUOTE_REQUEST_TOPICS } from '@/common/utils/quoteRequestTopics'
import { toast } from '@/common/utils/toast'
import {
  SUBDOMAIN_COOKIE_KEY,
  getRootDomainFromUrl,
  urlPathJoin,
  navigatingOnAvailableSubdomain,
} from '@/common/utils/url'
import { isUserMissingIdCheck } from '@/common/utils/users'
import Maintenance from '@/maintenance/Maintenance.vue'
import ProjectSidebar from '@/project/sidebar/ProjectSidebar.vue'
import DiscoverElectronicSafeDialog from '@/projects/dialogs/DiscoverElectronicSafeDialog.vue'
import ProjectTypeQuoteRequestDialog from '@/projects/dialogs/ProjectTypeQuoteRequestDialog.vue'
import ProjectsSidebar from '@/projects/sidebar/ProjectsSidebar.vue'
import {
  LOGIN_ARRAY_ROUTE_NAME,
  NO_AUTH_ROUTE_NAME,
  SIGNING_CHECKLIST_ADD_ROUTE_NAME,
  SIGN_IN_ROUTE_NAME,
  ROOM_DOCUMENT_VIEWER_ROUTE_NAME,
  ROOMS_ROUTE_NAME,
  TWO_FACTOR_ROUTE_NAME,
  TOKEN_SCOPES,
} from '@/router/routerData'
import { getCurrentRouteSafe } from '@/router/routerInstance'
import { STORE_PERSIST_KEY } from '@/store'
import { GlobalMutationTypes } from '@/store/modules/global/mutation_types'
import { SET_USER_TOKEN_SCOPE } from '@/store/modules/login/mutation_types'
import { SET_PROJECT_TYPE_QUOTE_REQUEST_DIALOG } from '@/store/modules/subscription/mutation_types'
import { GET_ACTIVITY_POLLING, GET_USER, GET_ZENDESK_AUTH } from '@/store/modules/user/action_types'
import { SET_SHOW_IDENTITY_CHECK_LANDING_MODAL, SET_SHOW_IDENTITY_CHECK_MODAL, SET_USER_PROFILE } from '@/store/modules/user/mutation_types'
import { useArchivesStore } from '@/stores/archivesStore'

import { AppStateEnum } from './enums'

const ACTIVITY_POLLING_DELAY = 30000

const routesWithoutConnectedLayout = [
  ROOM_DOCUMENT_VIEWER_ROUTE_NAME,
]

export default {
  name: 'App',
  components: {
    AIAssistantPane,
    AppBar,
    AppBarRoomBreadcrumb,
    AppFileUpload,
    AppFileUploadHint,
    AppFooter,
    AppLoader,
    AppToastsStack,
    AppVersionChecker,
    DiscoverElectronicSafeDialog,
    IdentityCheckingDialog,
    IdentityCheckingLandingDialog,
    Maintenance,
    ProjectTypeQuoteRequestDialog,
    ProjectsSidebar,
    UnauthorizedOverlay,
    ProjectSidebar,
  },
  setup() {
    const archivesStore = useArchivesStore()
    return { archivesStore }
  },
  errorCaptured (err) {
    if (!isNavigationFailure(err)) {
      console.error(err)
    } else {
      // Navigation "errors" are okay to log, but it's not necessarily an error
      console.info(err)
    }
    Sentry.captureException(err)
    return false
  },
  data () {
    return {
      SIGNING_CHECKLIST_ADD_ROUTE_NAME,
      activityPollingTimerId: 0,
      initialized: false,
      AppStateEnum,
    }
  },
  computed: {
    ...mapState('user', ['profile', 'showIdentityCheckModal', 'showIdentityCheckLandingModal']),
    ...mapState('maintenance', ['maintenanceModeEnabled']),
    ...mapState('login', ['userTokenScope']),
    ...mapState('subscription', ['projectTypeQuoteRequestDialog', 'subscriptionBranding', 'subscriptionBrandingPending']),
    ...mapState('appUploadHandler', ['uploadHint']),
    ...mapState('room', ['currentRoom', 'currentRoomPending', 'currentRoomRouteDecided']),
    ...mapState('global', ['isPendoLoaded', 'appState', 'homeLayout', 'projectLayout', 'routerHistory']),
    ...mapGetters('room', ['isCurrentUserPm']),
    ...mapGetters('user', ['canCurrentUserCreateDataroom', 'canCreateDataroomUnlimited', 'canCreateDataroomPayPerUse', 'canCurrentUserCreateProject', 'isArchivingEnabled']),
    ...mapGetters('login', ['isFullAccessLogin']),
    shouldDisplayNavigationDrawer () {
      return (this.homeLayout || this.projectLayout) && this.isFullAccessLogin
    },
    currentRoomNavigationPage () {
      return getRoomNavigationDataFromRoute(getCurrentRouteSafe())
    },
    shouldDisplayRoomBreadcrumb () {
      const currentRoute = getCurrentRouteSafe()
      return this.currentRoom && !!this.currentRoomNavigationPage && currentRoute.name !== ROOMS_ROUTE_NAME
    },
    usesConnectedLayout () {
      const currentRoute = getCurrentRouteSafe()
      return currentRoute.meta.requiresAuth || currentRoute.matched.map(x => x.meta.requiresAuth).includes(true)
    },
    isRouteWithoutConnectedLayout() {
      const currentRoute = getCurrentRouteSafe()
      return routesWithoutConnectedLayout.includes(currentRoute.name)
    },
    scopeAllowed () {
      return this.userTokenScope.includes(this.$route.meta.scopeAllowed)
    },
    appIsInitialized () {
      return this.appState === AppStateEnum.initialized
    },
    appIsLoading () {
      return this.appState === AppStateEnum.loading
    },
    appIsInError () {
      return this.appState === AppStateEnum.error
    },
    isPreviousRouteLogin () {
      const previousRoute = this.routerHistory.at(-1)
      return LOGIN_ARRAY_ROUTE_NAME.includes(previousRoute?.name)
    },
    // NOTE: This is a computed that checks for multiple possibilities where we shouldn't render
    // And show the Unauthorized modal.
    // By default, all logged-in routes need full access
    // If a scope is specified, then this scope can also access the route
    unauthorizedState () {
      return this.usesConnectedLayout && !this.isFullAccessLogin && !this.scopeAllowed
    },
    navigatingOnAvailableSubdomain () {
      return navigatingOnAvailableSubdomain()
    },
    // Those classes are there primarily to broadcast some app state to the DOM, especially user information
    // Those informations will then be available to Pendo and other third party integrations that can look into the DOM
    appClasses () {
      let appClasses = {}
      if (this.appIsInitialized) {
        // Init with top level classes
        appClasses = {
          'user-has-full-access-login': this.isFullAccessLogin,
          'user-is-sub-user': this.profile.isSubUser,
          'user-is-sub-admin': this.profile.isSubAdmin,
        }
        if (this.projectLayout && !this.currentRoomPending) {
          appClasses = {
            ...appClasses,
            'user-is-pm': this.isCurrentUserPm,
            // Added because it's a common use-case
            'user-is-guest': !this.isCurrentUserPm,
          }
        }
      }
      return appClasses
    },
    isGlobalLoaderOn () {
      return this.appIsLoading
        || this.subscriptionBrandingPending
        || this.currentRoomPending
        || (!this.currentRoomPending && this.currentRoomRouteDecided === false)
    },
  },
  watch: {
    // Uses a watcher so that if we first go to login, the logic is still done when out
    usesConnectedLayout: {
      async handler (newValue) {
        // We may already have a profile if just logged in or persisted
        if (this.profile.locale) {
          if (this.$i18n.locale !== this.profile.locale) {
            this.$i18n.locale = this.profile.locale
          }
        }
        if (this.profile.id) {
          try {
            Sentry.setUser({ id: this.profile.id })
          } catch {
            console.error('Could not set Sentry user ID')
          }
        }
        if (newValue) {
          this[GlobalMutationTypes.SET_APP_STATE](AppStateEnum.loading)

          try {
            if (!this.profile.id) {
              await this.GET_USER()

              if (this.profile.id && this.navigatingOnAvailableSubdomain) {
                this.clearLocalAuthState()
                globalThis.location = urlPathJoin(import.meta.env.VUE_APP_CLIENT_URL, this.$route.fullPath)
                return
              }

              if (this.profile.id) {
                setUserId(this.profile.id)
                if (!this.userTokenScope?.length) {
                  const allowedScope = this.$route.meta?.scopeAllowed
                  this.SET_USER_TOKEN_SCOPE([allowedScope || TOKEN_SCOPES.FULL_ACCESS])
                }
              }
            }

            if (this.profile.id) {
              if (this.isFullAccessLogin) {
                await this.GET_USER()
                if (this.profile.shouldPerformIdCheck && isUserMissingIdCheck(this.profile)) {
                  this.SET_SHOW_IDENTITY_CHECK_LANDING_MODAL(true)
                }
              }
              if (this.profile.locale) {
                if (this.$i18n.locale !== this.profile.locale) {
                  this.$i18n.locale = this.profile.locale
                }
              }

              this[GlobalMutationTypes.SET_APP_STATE](AppStateEnum.initialized)

              if (import.meta.PROD) {
                window.configureGtag('set', { user_id: this.profile.id })
              }
              if (this.isFullAccessLogin) {
                this.startActivityPolling()
              }

              try {
                this.GET_ZENDESK_AUTH()
              } catch {
                console.error('Couldn\'t set zendesk visitor info')
              }

              this.waitForPendoToBeLoaded()
            } else {
              this.redirectToSignInWithReturnRoute()
            }
          } catch (e) {
            console.error(e)
            if (e.request?.status === 401) {
              this[GlobalMutationTypes.SET_APP_STATE](AppStateEnum.initialized)
              if (e.response?.data?.error === TWO_FACTOR_AUTH_REQUIRED_ERROR) {
                this.redirectToTwoFactorWithReturnRoute()
              } else {
                this.redirectToSignInWithReturnRoute()
              }
            } else if (e.request?.status !== 503) {
              this[GlobalMutationTypes.SET_APP_STATE](AppStateEnum.error)
              toast.error(this.$t('App.initError'))
            }
          }
        } else {
          // If we triggered an init error but got back in login
          // The app shouldn't stay in an error state, we'll try again when we get back inside
          this[GlobalMutationTypes.SET_APP_STATE](AppStateEnum.initialized)
          clearTimeout(this.activityPollingTimerId)
        }
      },
      immediate: true,
    },
    '$i18n.locale': {
      handler (newValue) {
        document.documentElement.setAttribute('lang', newValue)
        dayjs.locale(newValue)
        // This also reloads guides so they can be displayed in the correct language
        // since we use the locale as a segment
        if (this.isPendoLoaded) {
          window.pendo.updateOptions({ visitor: { locale: newValue } })
        }

        try {
          if (window.zE) {
            window.zE('messenger:set', 'locale', newValue)
          }
        } catch (e) {
          console.error(e)
          console.info('can`t update zendesk locale')
        }
      },
    },
  },
  async created () {
    this.loadExternalScripts()
    try {
      // Checking for corrupted user state. This would bork a lot of things if we can't access the user.
      // User is always an object in its default state and is hydrated with data otherwise.
      // Profile may also (seen in V2-2071) be in a wrong form.
      // Context: V2-1373 and V2-2071
      if (!this.$store.state.user || !this.$store.state.user?.profile || !isObject(this.$store.state.user.profile)) {
        this.fatalStateErrorHandler()
        return
      }
    } catch (error) {
      // Wew, something _very_ wrong happened here !
      console.error(error)
      this.fatalStateErrorHandler()
    }
    const localeLanguage = this.$i18n.locale
    document.documentElement.setAttribute('lang', localeLanguage)
    dayjs.locale(localeLanguage)

    this.waitForZendeskToBeLoaded(localeLanguage)
  },
  mounted () {
    window.addEventListener('storage', () => {
      // If the profile value is undefined, and we get a null in storage, those are equal
      // eslint-disable-next-line eqeqeq
      if (getUserId() != this.profile?.id) {
        // Fully refresh if we were in a logged-in route
        // it seems we were deconnected elsewhere and have a new user
        if (!this.$route.matched.find((route) => route.name === NO_AUTH_ROUTE_NAME)) {
          this.$router.go()
        }
      }
    })
  },
  methods: {
    ...mapActions('user', [GET_ACTIVITY_POLLING, GET_USER, GET_ZENDESK_AUTH]),
    ...mapMutations('user', [SET_SHOW_IDENTITY_CHECK_MODAL, SET_SHOW_IDENTITY_CHECK_LANDING_MODAL, SET_USER_PROFILE]),
    ...mapMutations('login', [SET_USER_TOKEN_SCOPE]),
    ...mapMutations('subscription', [SET_PROJECT_TYPE_QUOTE_REQUEST_DIALOG]),
    ...mapMutations('global', [GlobalMutationTypes.SET_IS_PENDO_LOADED, GlobalMutationTypes.SET_APP_STATE]),
    redirectToSignInWithReturnRoute () {
      if (!this.usesConnectedLayout) {
        return
      }

      this.clearLocalAuthState()

      this.$router.push({
        name: SIGN_IN_ROUTE_NAME,
        params: {
          locale: this.$i18n.locale,
        },
        query: {
          ...this.$route.query,
          returnRoute: getCurrentRouteSafe().fullPath,
        },
      })
    },
    redirectToTwoFactorWithReturnRoute () {
      if (this.$route.name === TWO_FACTOR_ROUTE_NAME) {
        return
      }

      this.$router.push({
        name: TWO_FACTOR_ROUTE_NAME,
        query: {
          returnRoute: getCurrentRouteSafe().fullPath,
          locale: this.$i18n.locale,
        },
      })
    },
    clearLocalAuthState () {
      this.SET_USER_PROFILE({})
      this.SET_USER_TOKEN_SCOPE([])
      removeUserId()
      localStorage.removeItem(STORE_PERSIST_KEY)
    },
    loadExternalScripts () {
      // Pendo
      (function(apiKey){
        (function(p,e,n,d,o){var v,w,x,y,z;o=p[d]=p[d]||{};o._q=o._q||[];
          v=['initialize','identify','updateOptions','pageLoad','track'];for(w=0,x=v.length;w<x;++w)(function(m){
            o[m]=o[m]||function(){o._q[m===v[0]?'unshift':'push']([m].concat([].slice.call(arguments,0)));};})(v[w]);
          y=e.createElement(n);y.async=!0;y.src='https://cdn.eu.pendo.io/agent/static/'+apiKey+'/pendo.js';
          z=e.getElementsByTagName(n)[0];z.parentNode.insertBefore(y,z);})(window,document,'script','pendo');
      })(import.meta.env.VUE_APP_PENDO_KEY);

      // Matomo
      initMatomo()
    },
    initPendo () {
      try {
        const pendoOptions = {
          visitor: {
            id: import.meta.env.VUE_APP_ENVIRONNEMENT + '-' + this.profile.id,
            full_name: this.profile.fullName,
            email: this.profile.email,
            telephone: this.profile.telephone,
            locale: this.profile.locale,
            company: this.profile.company,
            isActive: this.profile.isActive,
            missingInfoToSign: this.profile.missingInfoToSign,
            shouldPerformIdCheck: this.profile.shouldPerformIdCheck,
            isArchivingAdmin: this.profile.isArchivingAdmin,
            isSubAdmin: this.profile.isSubAdmin,
            isSubArchivingAdmin: this.profile.isSubArchivingAdmin,
            isSubBillingAdmin: this.profile.isSubBillingAdmin,
            isSubUser: this.profile.isSubUser,
            hostname: location.hostname,
          },
          events: {
            ready: () => {
              // By only checking a variable we control that is set by Pendo itself,
              // we can be sure it _really_ loaded without checking Pendo directly
              // and possibly having V2-1803 triggering
              this.SET_IS_PENDO_LOADED(true)
            },
          },
        }

        if (this.profile.subscription) {
          pendoOptions.account = {
            id: import.meta.env.VUE_APP_ENVIRONNEMENT + '-' + this.profile.subscription.id,
            name: this.profile.subscription.name,
            company: this.profile.subscription.company,
            address1: this.profile.subscription.address1,
            address2: this.profile.subscription.address2,
            city: this.profile.subscription.city,
            zip: this.profile.subscription.zip,
            country: this.profile.subscription.country,
            archivingEnabled: this.isArchivingEnabled,
            isDRSub: this.canCurrentUserCreateDataroom,
            hasActiveDR: this.profile.subscription.hasActiveDR,
            canCreateDRPack: this.canCreateDataroomUnlimited,
            canCreateDRPPU: this.canCreateDataroomPayPerUse,
            isTMSub: this.canCurrentUserCreateProject,
            isM365Sub: this.profile.subscription.wopiOfficeEnabled,
          }
        }
        window.pendo.initialize(pendoOptions)
      } catch (error) {
        console.info('Couldn\'t set Pendo visitor info')
        console.error(error)
      }
    },
    initZendesk () {
      try {
        const scriptTag = document.createElement('script')
        scriptTag.type = 'text/javascript'
        scriptTag.defer = true
        scriptTag.id = 'ze-snippet'
        scriptTag.src = 'https://static.zdassets.com/ekr/snippet.js?key=dc3651e4-1249-472e-89ca-b78b5a76ca41'

        const s = document.getElementsByTagName('script')[0]
        s.parentNode.insertBefore(scriptTag, s)
      } catch (e) {
        console.error(e)
        console.info('zendesk init failed')
      }
    },
    waitForPendoToBeLoaded () {
      this.checkForPendoInterval = setInterval(() => {
        if (window.pendo && this.profile?.id) {
          this.initPendo()
          clearInterval(this.checkForPendoInterval)
        }
      }, 200)
    },
    waitForZendeskToBeLoaded (localeLanguage) {
      this.initZendesk()

      this.zendeskInterval = setInterval(() => {
        if (window.zE) {
          window.zE('messenger:set', 'locale', localeLanguage)
          clearInterval(this.zendeskInterval)
        }
      }, 200)
    },
    startActivityPolling () {
      try {
        this.GET_ACTIVITY_POLLING()
      } finally {
        this.activityPollingTimerId = setTimeout(() => {
          this.startActivityPolling()
        }, ACTIVITY_POLLING_DELAY)
      }
    },
    fatalStateErrorHandler () {
      this[GlobalMutationTypes.SET_APP_STATE](AppStateEnum.error)
      console.error('Broken state ! Forcefully resetting it.')
      Sentry.setUser({ id: getUserId() })
      Sentry.captureMessage('😨 The state somehow got corrupted', {
        contexts: {
          'User Object': this.$store.state.user,
        },
      })
      // Same logic as in when we do it in the regular logout
      // The instructions are done in an immediately queued timer so that the persist already handled the changes it needed to do
      // And we can then remove it entirely
      setTimeout(() => {
        this.removeSubdomainDataCookie()
        localStorage.removeItem(STORE_PERSIST_KEY)
        removeUserId()
        const locationUrl = new URL(window.location.href)
        locationUrl.searchParams.set('isVoluntaryLogOut', 'false')
        locationUrl.searchParams.set('isLogOut', 'true')
        // Boot the app again, it'll do the logout sequence and we'll make it look like an expired session.
        window.location = locationUrl.toString()
      }, 0)
    },
    removeSubdomainDataCookie () {
      const clientAppUrl = import.meta.env.VUE_APP_CLIENT_URL
      const cookieDomain = getRootDomainFromUrl(clientAppUrl)
      this.$cookies.remove(SUBDOMAIN_COOKIE_KEY, null, cookieDomain)
    },
    openRequestElectronicSafeQuoteDialog () {
      this.SET_PROJECT_TYPE_QUOTE_REQUEST_DIALOG({
        isOpen: true,
        defaultProjectType: QUOTE_REQUEST_TOPICS.ELECTRONIC_SAFE,
      })
    },
  },
}
</script>

<style lang="scss">
@import "scss/main";

// input--indeterminate is set in _vuetify-override.js
// Disable checklists have this default color rgba(255, 114, 118, 0.38)
// For vuetify theme customization, we don't want to keep this behavior
.input-selection-indeterminate {
  .theme--light.v-input--selection-controls.v-input--disabled:not(.v-input--indeterminate) .v-icon {
    color: rgba(0, 0, 0, 0.38) !important;
  }
}

html {
  font-size: 14px;
}

.app-main-content {
  flex-grow: 1;
  min-height: 0;
}

.upload-hint-fade-leave-active {
  transition: all .12s ease-in-out;
}

.upload-hint-fade-leave-to {
  bottom: 15px !important;
  opacity: 0;
}

.Closd-hoverArea {
  cursor: pointer;
  padding: .25rem;
  border: 1px dashed transparent;
}

.selected-items-bar {
  display: flex;
  border: 1px solid rgb(var(--v-theme-primary));
  align-items: center;
  flex-wrap: wrap;
  padding: 0 $spacer * 5;
  border-radius: $border-radius-root * 2;
}

.selected-items-count {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.Closd-hoverArea:hover {
  background-color: #f0f0f0;
  border-color: #c2c2c2;
}

.v-application--is-ltr .v-text-field__suffix {
  margin-right: $spacer * 4;
}

.v-alert__content ul {
  list-style-type: disc;
  padding-left: 24px;
}
</style>
